// src/components/BulkEntries.js
import React, { useState, useEffect, useCallback } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { useAuth } from '../auth/AuthContext';
import auditLogger from '../utils/auditLogger';
import './BulkEntries.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const BulkEntries = () => {
  const { selectedCompetition } = useCompetition();
  const { currentUser } = useAuth();

  // Helper function to format dancer names - moved to top to fix hoisting issue
  const formatDancerName = (couple) => {
    if (couple.name && couple.name.trim() && couple.name !== 'undefined' && !couple.name.includes('undefined')) {
      return couple.name.trim();
    }

    if (couple.dancerType === 'solo') {
      return couple.leadName || 'Unknown Dancer';
    } else if (couple.dancerType === 'team') {
      if (couple.teamMembers && couple.teamMembers.length > 0) {
        return couple.teamMembers.join(', ');
      }
      return couple.leadName || 'Unknown Team';
    } else {
      if (couple.leadName && couple.followName && couple.followName !== 'undefined') {
        return `${couple.leadName} & ${couple.followName}`;
      } else if (couple.leadName && couple.leadName !== 'undefined') {
        return couple.leadName;
      } else if (couple.followName && couple.followName !== 'undefined') {
        return couple.followName;
      }
    }

    return `Dancer ${couple.clothNumber}`;
  };

  // State management
  const [clubs, setClubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [existingNumbers, setExistingNumbers] = useState([]);
  const [existingDancers, setExistingDancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Form state
  const [selectedClub, setSelectedClub] = useState('');
  const [dancers, setDancers] = useState([]);

  // Search states
  const [clubSearch, setClubSearch] = useState('');
  const [existingDancerSearch, setExistingDancerSearch] = useState('');

  // Loading states for operations
  const [isDeletingDancer, setIsDeletingDancer] = useState(null); // Track which dancer is being deleted

  // Initialize with one empty dancer entry
  useEffect(() => {
    const timestamp = Date.now();
    setDancers([{
      id: `dancer-${timestamp}-0`,
      dancerType: 'solo',
      leadName: '',
      followName: '',
      teamMembers: ['', '', '', '', '', ''],
      country: 'South Africa',
      selectedEvent: '',
      eventSearch: ''
    }]);
  }, []);

  // Fetch clubs, events, and existing cloth numbers
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch clubs
        const clubsResponse = await fetch(`https://${_host}:6003/api/${_company}/clubs`);
        if (clubsResponse.ok) {
          const clubsData = await clubsResponse.json();
              setClubs(clubsData.filter(club =>
            !selectedCompetition || (club.competitionId || club.competition_id) === selectedCompetition.id
          ));
        } else {
          console.error('Failed to fetch clubs:', clubsResponse.status);
        }

        // Fetch events
        const eventsResponse = await fetch(`https://${_host}:6003/api/${_company}/events`);
        if (eventsResponse.ok) {
          const eventsData = await eventsResponse.json();
          const filteredEvents = eventsData.filter(event => {
            const eventCompetitionId = event.competitionId || event.competition_id;
            return !selectedCompetition || eventCompetitionId === selectedCompetition.id;
          });
          setEvents(filteredEvents);
        } else {
          console.error('Failed to fetch events:', eventsResponse.status);
        }

        // Fetch existing cloth numbers and dancers
        const couplesResponse = await fetch(`https://${_host}:6003/api/${_company}/couples`);
        if (couplesResponse.ok) {
          const couplesData = await couplesResponse.json();
          const filteredCouples = selectedCompetition
            ? couplesData.filter(couple => (couple.competitionId || couple.competition_id) === selectedCompetition.id)
            : couplesData;

          const numbers = filteredCouples.map(couple => parseInt(couple.clothNumber || couple.cloth_number)).filter(n => !isNaN(n));
            setExistingNumbers(numbers);
          setExistingDancers(filteredCouples);
        } else {
          console.error('Failed to fetch couples:', couplesResponse.status);
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedCompetition]);

  // Cleanup function to handle component unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts when component unmounts
      // This prevents memory leaks and DOM manipulation issues
    };
  }, []);

  // Filter clubs by search term
  const filteredClubs = clubs.filter(club => {
    const searchLower = clubSearch.toLowerCase();
    const name = club.name?.toLowerCase() || '';
    return name.includes(searchLower);
  });

  
  // Filter existing dancers by selected club and search term
  const filteredExistingDancers = existingDancers.filter(dancer => {
    // Support both camelCase and snake_case field names for club ID
    const dancerClubId = dancer.clubId || dancer.club_id;
    const matchesClub = !selectedClub || dancerClubId === selectedClub;

    const searchLower = existingDancerSearch.toLowerCase();
    const name = formatDancerName(dancer).toLowerCase();
    // Support both camelCase and snake_case field names for cloth number
    const clothNumber = (dancer.clothNumber || dancer.cloth_number)?.toString() || '';
    const matchesSearch = !existingDancerSearch ||
      name.includes(searchLower) || clothNumber.includes(searchLower);

    return matchesClub && matchesSearch;
  }).sort((a, b) => {
    // Sort by cloth number (support both field names)
    const aNum = parseInt(a.clothNumber || a.cloth_number) || 0;
    const bNum = parseInt(b.clothNumber || b.cloth_number) || 0;
    return aNum - bNum;
  });

  
  // Remove existing dancer
  const removeExistingDancer = async (dancerId, dancerName) => {
    try {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete "${dancerName}"? This action cannot be undone.`
      );

      if (!confirmDelete) return;

      setIsDeletingDancer(dancerId);
      setError(null);

      const response = await fetch(`https://${_host}:6003/api/${_company}/couples/${dancerId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete dancer');
      }

      // Remove from events if allocated
      const dancerEvents = events.filter(event => {
        // Handle dancers field that might be JSON string or array
        let dancers = [];
        if (event.dancers) {
          if (typeof event.dancers === 'string') {
            try {
              dancers = JSON.parse(event.dancers);
            } catch (error) {
              console.error('Failed to parse event dancers JSON:', error);
              dancers = [];
            }
          } else if (Array.isArray(event.dancers)) {
            dancers = event.dancers;
          }
        }

        return dancers.some(d => d.coupleId === dancerId);
      });

      for (const event of dancerEvents) {
        // Handle dancers field that might be JSON string or array
        let dancers = [];
        if (event.dancers) {
          if (typeof event.dancers === 'string') {
            try {
              dancers = JSON.parse(event.dancers);
            } catch (error) {
              console.error('Failed to parse event dancers JSON:', error);
              dancers = [];
            }
          } else if (Array.isArray(event.dancers)) {
            dancers = event.dancers;
          }
        }

        const updatedDancers = dancers.filter(d => d.coupleId !== dancerId);
        await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dancers: updatedDancers })
        });
      }

      // Log the deletion
      if (currentUser) {
        auditLogger.logDeleteDancer(
          currentUser.username || currentUser.id,
          dancerName,
          dancerId
        );
      }

      setSuccess(`Successfully deleted "${dancerName}"`);
      setTimeout(() => setSuccess(null), 3000);

      // Refetch data
      const couplesResponse = await fetch(`https://${_host}:6003/api/${_company}/couples`);
      if (couplesResponse.ok) {
        const couplesData = await couplesResponse.json();
        const filteredCouples = selectedCompetition
          ? couplesData.filter(couple => couple.competitionId === selectedCompetition.id)
          : couplesData;
        setExistingDancers(filteredCouples);

        const numbers = filteredCouples.map(couple => parseInt(couple.clothNumber));
        setExistingNumbers(numbers);
      }

    } catch (err) {
      console.error('Error deleting dancer:', err);
      setError('Failed to delete dancer. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsDeletingDancer(null);
    }
  };

  // Generate available cloth numbers
  const generateAvailableNumbers = useCallback((count) => {
    const availableNumbers = [];
    let currentNumber = 1;

    console.log('🔍 DEBUG: Generating available numbers:', {
      requestedCount: count,
      existingNumbers: existingNumbers,
      existingNumbersCount: existingNumbers.length
    });

    while (availableNumbers.length < count && currentNumber <= 999) {
      if (!existingNumbers.includes(currentNumber)) {
        availableNumbers.push(currentNumber);
      }
      currentNumber++;
    }

    console.log('🔍 DEBUG: Generated available numbers:', availableNumbers);

    if (availableNumbers.length < count) {
      console.error('❌ ERROR: Not enough available cloth numbers');
      throw new Error(`Only ${availableNumbers.length} cloth numbers available, but ${count} dancers need numbers`);
    }

    return availableNumbers;
  }, [existingNumbers]);

  // Get filtered events for each dancer based on their search term
  const getFilteredEvents = (eventSearch) => {
    return events.filter(event => {
      const searchLower = eventSearch.toLowerCase();
      const name = event.name?.toLowerCase() || '';
      // Support both camelCase and snake_case field names for compatibility
      const itemNumber = (event.itemNumber || event.item_number)?.toString() || '';
      return name.includes(searchLower) || itemNumber.includes(searchLower);
    }).sort((a, b) => {
      // Support both camelCase and snake_case field names for sorting
      const aNum = parseInt(a.itemNumber || a.item_number) || 999;
      const bNum = parseInt(b.itemNumber || b.item_number) || 999;
      return aNum - bNum;
    });
  };

  // Add new dancer entry
  const addDancerEntry = useCallback(() => {
    if (isProcessing) return; // Prevent adding during processing

    const timestamp = Date.now();
    const newDancer = {
      id: `dancer-${timestamp}-${Math.random()}`,
      dancerType: 'solo',
      leadName: '',
      followName: '',
      teamMembers: ['', '', '', '', '', ''],
      country: 'South Africa',
      selectedEvent: '',
      eventSearch: ''
    };
    setDancers(prevDancers => {
      const newDancers = [...prevDancers, newDancer];
      return newDancers;
    });
  }, [isProcessing]);

  // Remove dancer entry
  const removeDancerEntry = useCallback((id) => {
    if (isProcessing) return; // Prevent removal during processing
    setDancers(prevDancers => prevDancers.filter(d => d.id !== id));
  }, [isProcessing]);

  // Update dancer field
  const updateDancerField = useCallback((id, field, value) => {
    if (isProcessing && field !== 'eventSearch') return; // Allow search during processing but not other changes

    setDancers(prevDancers => {
      return prevDancers.map(dancer =>
        dancer.id === id ? { ...dancer, [field]: value } : dancer
      );
    });
  }, [isProcessing]);

  // Validate form before processing
  const validateForm = () => {
    if (!selectedClub) {
      setError('Please select a club/studio');
      return false;
    }

    const validDancers = dancers.filter(dancer => {
      const hasName = dancer.dancerType === 'team'
        ? dancer.teamMembers.some(m => m.trim())
        : dancer.leadName.trim();

      return hasName;
    });

    if (validDancers.length === 0) {
      setError('Please add at least one dancer with valid names');
      return false;
    }

    // Check if all dancers have events selected (optional allocation)
    // Note: Event selection is optional, dancers can be created without allocation
    const dancersWithEvents = validDancers.filter(d => d.selectedEvent);
    if (dancersWithEvents.length > 0) {
      console.log(`Processing ${dancersWithEvents.length} dancers with event allocation`);
    } else {
      console.log('Processing dancers without event allocation');
    }

    return true;
  };

  // Check if form is valid for button state
  const isFormValid = () => {
    if (!selectedClub || dancers.length === 0) return false;

    const validDancers = dancers.filter(dancer => {
      const hasName = dancer.dancerType === 'team'
        ? dancer.teamMembers.some(m => m.trim())
        : dancer.leadName.trim();

      return hasName;
    });

    return validDancers.length > 0;
  };

  // Process bulk entries
  const handleBulkProcess = async () => {
    if (!validateForm()) return;

    // Prevent multiple simultaneous processes
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      setError(null);
      setSuccess(null);

      const validDancers = dancers.filter(dancer => {
        const hasName = dancer.dancerType === 'team'
          ? dancer.teamMembers.some(m => m.trim())
          : dancer.leadName.trim();
        return hasName;
      });

      // Generate cloth numbers
      const clothNumbers = generateAvailableNumbers(validDancers.length);

      // Prepare dancer data for API
      const dancerData = validDancers.map((dancer, index) => {
        // Map dancer types to database format
        let mappedDancerType = dancer.dancerType;
        if (dancer.dancerType === 'duo') {
          mappedDancerType = 'couple'; // Convert duo to couple for database storage
        }

        const baseData = {
          // Use both camelCase and snake_case for maximum compatibility
          clothNumber: clothNumbers[index].toString().padStart(3, '0'),
          cloth_number: clothNumbers[index].toString().padStart(3, '0'),
          dancerType: mappedDancerType,
          dancer_type: mappedDancerType,
          name: formatDancerName(dancer),
          country: dancer.country,
          clubId: selectedClub,
          club_id: selectedClub,
          competitionId: selectedCompetition?.id || null,
          competition_id: selectedCompetition?.id || null,
          clubName: clubs.find(c => c.id === selectedClub)?.name || '',
          club_name: clubs.find(c => c.id === selectedClub)?.name || '',
          registrationCheckin: false,
          marshallCheckin: false,
          // Store original event selection for debugging
          _originalEventId: dancer.selectedEvent,
          _originalIndex: index,
          // Store original dancer type for debugging
          _originalDancerType: dancer.dancerType
        };

        // Add dancer-specific fields (use original dancer type for logic)
        if (dancer.dancerType === 'solo') {
          baseData.leadName = dancer.leadName.trim();
          baseData.lead_name = dancer.leadName.trim();
          baseData.followName = '';
          baseData.follow_name = '';
        } else if (dancer.dancerType === 'duo' || dancer.dancerType === 'couple') {
          baseData.leadName = dancer.leadName.trim();
          baseData.lead_name = dancer.leadName.trim();
          baseData.followName = dancer.followName.trim();
          baseData.follow_name = dancer.followName.trim();
        } else if (dancer.dancerType === 'team') {
          baseData.teamMembers = dancer.teamMembers.filter(m => m.trim());
          baseData.team_members = dancer.teamMembers.filter(m => m.trim());
          baseData.leadName = dancer.teamMembers[0] || '';
          baseData.lead_name = dancer.teamMembers[0] || '';
        }

        return baseData;
      });

  
      // Validate no duplicate cloth numbers before attempting creation
      const proposedNumbers = dancerData.map(d => d.clothNumber);
      const duplicateExistingCheck = proposedNumbers.filter(num => existingNumbers.includes(parseInt(num)));
      if (duplicateExistingCheck.length > 0) {
        console.error('❌ ERROR: Proposed cloth numbers already exist in database:', duplicateExistingCheck);
        throw new Error(`Cannot create dancers - cloth numbers already exist: ${duplicateExistingCheck.join(', ')}`);
      }

      // Also check for duplicates within the current batch
      const duplicateBatchCheck = proposedNumbers.filter((num, index) => proposedNumbers.indexOf(num) !== index);
      if (duplicateBatchCheck.length > 0) {
        console.error('❌ ERROR: Duplicate cloth numbers in current batch:', duplicateBatchCheck);
        throw new Error(`Cannot create dancers - duplicate cloth numbers in this batch: ${duplicateBatchCheck.join(', ')}`);
      }

      console.log('✅ Validation passed - no duplicate cloth numbers');

      // Create dancers via API
      const createPromises = dancerData.map(async (data, index) => {
        console.log(`Creating dancer ${index + 1}/${dancerData.length}:`, {
          name: data.name,
          clothNumber: data.clothNumber,
          selectedEventId: data._originalEventId,
          dancerType: data.dancerType,
          originalDancerType: data._originalDancerType
        });

        const response = await fetch(`https://${_host}:6003/api/${_company}/couples`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Failed to create dancer ${data.name}:`, {
            status: response.status,
            errorText
          });
          throw new Error(`Failed to create dancer: ${data.name}`);
        }

        const createdDancer = await response.json();
        console.log(`Successfully created dancer:`, {
          name: createdDancer.name,
          id: createdDancer.id,
          clothNumber: createdDancer.clothNumber
        });

        return createdDancer;
      });

      const createdDancers = await Promise.all(createPromises);
      console.log('All dancers created successfully:', {
        totalCreated: createdDancers.length,
        createdDancers: createdDancers.map(d => ({
          name: d.name,
          id: d.id,
          clothNumber: d.clothNumber
        }))
      });

      // Group dancers by selected event to handle allocations properly
      const dancersByEvent = {};
      createdDancers.forEach((dancer, index) => {
        const originalDancer = validDancers[index];
        const selectedEventId = originalDancer.selectedEvent;

        if (selectedEventId) {
          if (!dancersByEvent[selectedEventId]) {
            dancersByEvent[selectedEventId] = [];
          }
          dancersByEvent[selectedEventId].push(dancer);
        } else {
          console.log(`Skipping allocation for dancer ${dancer.name} - no event selected`);
        }
      });

      console.log('📋 Event allocation plan:', Object.keys(dancersByEvent).map(eventId => ({
        eventId,
        dancerCount: dancersByEvent[eventId].length,
        dancerNames: dancersByEvent[eventId].map(d => d.name)
      })));

      // Allocate dancers to events by event (to avoid race conditions)
      const allocationPromises = Object.entries(dancersByEvent).map(async ([eventId, dancersForEvent]) => {
        const event = events.find(e => e.id === eventId);

        if (!event) {
          console.error(`Event not found for allocation: ${eventId}`);
          throw new Error(`Event not found for allocation: ${eventId}`);
        }

        console.log(`🎯 Allocating ${dancersForEvent.length} dancers to event: ${event.name}`);

        // Get current event data to avoid race conditions
        const eventResponse = await fetch(`https://${_host}:6003/api/${_company}/events/${eventId}`);
        if (!eventResponse.ok) {
          throw new Error(`Failed to fetch current event data for ${event.name}`);
        }

        const currentEventData = await eventResponse.json();

        // Parse existing dancers from the current event data
        let existingDancers = [];
        if (currentEventData.dancers) {
          if (typeof currentEventData.dancers === 'string') {
            try {
              existingDancers = JSON.parse(currentEventData.dancers);
            } catch (error) {
              console.error('Failed to parse current event dancers JSON:', error);
              existingDancers = [];
            }
          } else if (Array.isArray(currentEventData.dancers)) {
            existingDancers = currentEventData.dancers;
          }
        }

        // Add all new dancers to this event
        const newDancerEntries = dancersForEvent.map(dancer => ({
          coupleId: dancer.id,
          // Use field names that display system expects (snake_case for cloth number)
          cloth_number: dancer.clothNumber,
          clothNumber: dancer.clothNumber, // Also include camelCase for compatibility
          name: dancer.name,
          dancerType: dancer.dancerType,
          // Include additional fields that might be needed for display
          leadName: dancer.leadName,
          followName: dancer.followName
        }));

        const updatedEventDancers = [...existingDancers, ...newDancerEntries];

        console.log(`📝 Updating event ${event.name} with ${newDancerEntries.length} new dancers (total: ${updatedEventDancers.length})`);

        // Update the event once with all dancers for this event
        const response = await fetch(`https://${_host}:6003/api/${_company}/events/${eventId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dancers: updatedEventDancers })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Failed to allocate dancers to event:`, {
            eventId: eventId,
            eventName: event.name,
            dancerCount: dancersForEvent.length,
            status: response.status,
            errorText
          });
          throw new Error(`Failed to allocate dancers to event: ${event.name}`);
        }

        const updatedEvent = await response.json();
        console.log(`✅ Successfully allocated ${dancersForEvent.length} dancers to event ${event.name}`);

        return {
          event: updatedEvent,
          allocatedDancers: dancersForEvent.length
        };
      });

      const allocationResults = await Promise.all(allocationPromises);

      // Log allocation summary
      const totalAllocated = allocationResults.reduce((sum, result) => sum + result.allocatedDancers, 0);
      console.log(`🎉 Allocation complete: ${totalAllocated} dancers allocated to ${allocationResults.length} events`);

      // Log the bulk operation
      if (currentUser) {
        auditLogger.log(
          currentUser.username || currentUser.id,
          'Bulk Entry Processing',
          `Processed ${validDancers.length} dancers for club: ${clubs.find(c => c.id === selectedClub)?.name}`,
          selectedClub,
          'BulkEntry'
        );
      }

      const allocatedDancersCount = allocationResults.reduce((sum, result) => sum + result.allocatedDancers, 0);
      const unallocatedCount = validDancers.length - allocatedDancersCount;

      let successMessage = `Successfully processed ${validDancers.length} dancers!`;
      if (allocatedDancersCount > 0) {
        successMessage += ` ${allocatedDancersCount} allocated to events.`;
      }
      if (unallocatedCount > 0) {
        successMessage += ` ${unallocatedCount} created without event allocation.`;
      }

      setSuccess(successMessage);

      // Reset form with a more robust approach
      const resetForm = () => {
        const timestamp = Date.now();
        const newDancer = {
          id: `dancer-${timestamp}-0`,
          dancerType: 'solo',
          leadName: '',
          followName: '',
          teamMembers: ['', '', '', '', '', ''],
          country: 'South Africa',
          selectedEvent: '',
          eventSearch: ''
        };

        // Reset states in proper order to prevent conflicts
        setDancers([newDancer]);
        setSelectedClub('');
        setClubSearch('');
      };

      // Use setTimeout to ensure DOM has time to process
      setTimeout(resetForm, 100);

      setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
      console.error('Error in bulk processing:', err);
      setError(err.message || 'Failed to process bulk entries. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  // Cleanup timeout references when component unmounts
  useEffect(() => {
    let timeoutIds = [];
    return () => {
      timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
    };
  }, []);

  if (loading) {
    return (
      <div className="bulk-entries-container">
        <div className="loading-state">
          <div className="loading-spinner">⏳</div>
          <p>Loading clubs and events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-entries-container">
      <div className="bulk-entries-header">
        <h2>Bulk Entries Management</h2>
        <p className="subtitle">Add multiple dancers at once with automatic cloth number generation and event allocation</p>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="error-message">{error}</div>
      )}
      {success && (
        <div className="success-message">{success}</div>
      )}

      {/* Club Selection and Header Controls */}
      <div className="club-and-controls-section">
        <div className="club-selection-area">
          <h3>Select Club/Studio</h3>
          <input
            type="text"
            className="club-search-input"
            placeholder="Search clubs and studios..."
            value={clubSearch}
            onChange={(e) => setClubSearch(e.target.value)}
            disabled={isProcessing}
          />
          <select
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
            className="club-select"
            disabled={isProcessing}
          >
            <option value="">Choose a club/studio...</option>
            {filteredClubs.map(club => (
              <option key={club.id} value={club.id}>
                {club.name}
              </option>
            ))}
          </select>
        </div>
        <div className="add-dancer-control">
          <button
            className="add-dancer-btn"
            onClick={addDancerEntry}
            disabled={isProcessing}
          >
            + Add Dancer
          </button>
        </div>
      </div>

      {/* Two-Panel Layout */}
      <div className="bulk-panels-container">
        {/* Left Panel: Add New Dancers */}
        <div className="bulk-panel left-panel">
          <div className="panel-header">
            <h3>Add New Dancers</h3>
          </div>
          <div className="dancer-entries-section">
            <div className="dancers-list">
              {dancers.map((dancer, index) => (
            <div key={dancer.id} className="dancer-entry-card" data-index={index}>
              <div className="dancer-entry-header">
                <h4>Dancer #{index + 1}</h4>
                {dancers.length > 1 && (
                  <button
                    className="remove-dancer-btn"
                    onClick={() => removeDancerEntry(dancer.id)}
                    disabled={isProcessing}
                    title="Remove dancer"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Dancer Type Selection */}
              <div className="dancer-type-section">
                <label>Dancer Type:</label>
                <div className="dancer-type-options">
                  {['solo', 'duo', 'team'].map(type => (
                    <label key={`${dancer.id}-${type}`} className="dancer-type-option">
                      <input
                        type="radio"
                        name={`dancer-type-${dancer.id}`}
                        value={type}
                        checked={dancer.dancerType === type}
                        onChange={(e) => updateDancerField(dancer.id, 'dancerType', e.target.value)}
                        disabled={isProcessing}
                      />
                      <span className={`type-label ${type}`}>
                        {type === 'duo' ? 'Duo/Couple' : type.charAt(0).toUpperCase() + type.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Name Fields */}
              {dancer.dancerType === 'team' ? (
                <div className="team-members-section">
                  <label>Team Members:</label>
                  {dancer.teamMembers.slice(0, 5).map((member, memberIndex) => (
                    <input
                      key={`${dancer.id}-member-${memberIndex}`}
                      type="text"
                      placeholder={`Team Member ${memberIndex + 1}`}
                      value={member}
                      onChange={(e) => {
                        const newTeamMembers = [...dancer.teamMembers];
                        newTeamMembers[memberIndex] = e.target.value;
                        updateDancerField(dancer.id, 'teamMembers', newTeamMembers);
                      }}
                      disabled={isProcessing}
                    />
                  ))}
                </div>
              ) : (
                <div className="name-fields">
                  <div className="name-field">
                    <label>{dancer.dancerType === 'solo' ? 'Dancer Name:' : 'Lead Name:'}</label>
                    <input
                      type="text"
                      placeholder={`Enter ${dancer.dancerType === 'solo' ? 'dancer' : 'lead'} name`}
                      value={dancer.leadName}
                      onChange={(e) => updateDancerField(dancer.id, 'leadName', e.target.value)}
                      disabled={isProcessing}
                    />
                  </div>
                  {(dancer.dancerType === 'duo' || dancer.dancerType === 'couple') && (
                    <div className="name-field">
                      <label>Follow Name:</label>
                      <input
                        type="text"
                        placeholder="Enter follow name"
                        value={dancer.followName}
                        onChange={(e) => updateDancerField(dancer.id, 'followName', e.target.value)}
                        disabled={isProcessing}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Event Selection */}
              <div className="event-selection-section">
                <label>Select Event:</label>
                <input
                  type="text"
                  className="event-search-input"
                  placeholder="Search events by name or number..."
                  value={dancer.eventSearch}
                  onChange={(e) => updateDancerField(dancer.id, 'eventSearch', e.target.value)}
                  disabled={isProcessing}
                />
                <select
                  value={dancer.selectedEvent}
                  onChange={(e) => updateDancerField(dancer.id, 'selectedEvent', e.target.value)}
                  className="event-select"
                  disabled={isProcessing}
                >
                  <option value="">Choose an event...</option>
                  {getFilteredEvents(dancer.eventSearch).map(event => (
                    <option key={`${dancer.id}-event-${event.id}`} value={event.id}>
                      {(event.itemNumber || event.item_number)} - {event.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Country Selection */}
              <div className="country-section">
                <label>Country:</label>
                <input
                  type="text"
                  value={dancer.country}
                  onChange={(e) => updateDancerField(dancer.id, 'country', e.target.value)}
                  placeholder="Enter country"
                  disabled={isProcessing}
                />
              </div>
            </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel: Existing Dancers */}
        <div className="bulk-panel right-panel">
          <div className="panel-header">
            <h3>Existing Dancers</h3>
            <span className="panel-subtitle">
              {selectedClub ? clubs.find(c => c.id === selectedClub)?.name : 'All Clubs'}
              {' '} ({filteredExistingDancers.length} dancers)
            </span>
          </div>

          {/* Existing Dancers Search */}
          <div className="existing-dancers-search">
            <input
              type="text"
              className="search-input"
              placeholder="Search by cloth number or name..."
              value={existingDancerSearch}
              onChange={(e) => setExistingDancerSearch(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          {/* Existing Dancers List */}
          <div className="existing-dancers-list">
            {selectedClub && filteredExistingDancers.length === 0 ? (
              <p className="empty-message">No dancers found for this club.</p>
            ) : !selectedClub ? (
              <p className="empty-message">Please select a club to view existing dancers.</p>
            ) : (
              filteredExistingDancers.map(dancer => (
                <div key={dancer.id} className={`existing-dancer-card ${isDeletingDancer === dancer.id ? 'deleting' : ''}`}>
                  <div className="existing-dancer-number">{dancer.clothNumber}</div>
                  <div className="existing-dancer-info">
                    <div className="existing-dancer-name">{formatDancerName(dancer)}</div>
                    <div className="existing-dancer-meta">
                      <span className="dancer-type-badge">{dancer.dancerType || 'Couple'}</span>
                      <span className="dancer-country">{dancer.country}</span>
                    </div>
                    {(() => {
                      // Check if dancer is allocated to any events
                      const isAllocated = events.some(event => {
                        // Handle dancers field that might be JSON string or array
                        let dancers = [];
                        if (event.dancers) {
                          if (typeof event.dancers === 'string') {
                            try {
                              dancers = JSON.parse(event.dancers);
                            } catch (error) {
                              dancers = [];
                            }
                          } else if (Array.isArray(event.dancers)) {
                            dancers = event.dancers;
                          }
                        }
                        return dancers.some(d => d.coupleId === dancer.id);
                      });

                      return isAllocated && <span className="allocated-indicator">Allocated to events</span>;
                    })()}
                  </div>
                  <div className="existing-dancer-actions">
                    <button
                      className={`remove-existing-btn ${isDeletingDancer === dancer.id ? 'loading' : ''}`}
                      onClick={() => removeExistingDancer(dancer.id, formatDancerName(dancer))}
                      title={isDeletingDancer === dancer.id ? 'Deleting...' : 'Delete dancer'}
                      disabled={isDeletingDancer === dancer.id}
                    >
                      {isDeletingDancer === dancer.id ? (
                        <span className="deleting-spinner">⏳</span>
                      ) : (
                        '×'
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Bulk Process Button */}
      <div className="process-section">
        <button
          className="bulk-process-btn"
          onClick={handleBulkProcess}
          disabled={isProcessing || !isFormValid()}
        >
          {isProcessing ? (
            <>
              <span className="processing-spinner">⏳</span>
              Processing...
            </>
          ) : (
            <>
              Generate Cloth Numbers & Process {dancers.filter(d => {
                const hasName = d.dancerType === 'team' ? d.teamMembers.some(m => m.trim()) : d.leadName.trim();
                return hasName;
              }).length} Dancers
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BulkEntries;